import React, { PropsWithChildren, useEffect, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { useDialog } from '@smart-builder/components/src/dialog';
import { _t } from '@smart-builder/util';
import {
  apiLogin,
  apiLoginSSO,
  buildSSOBody,
  extractSSOParams,
  getSessionToken,
  ResponseCodes,
} from '@smart-builder/rest';
import { DialogType } from '@smart-builder/components';
import { AuthorizedApp } from './LoginForm';

export const LoginSSO: React.FC = () => {
  const { dialogOk } = useDialog();
  useEffect(() => {
    const allowedApps = (SB_CONFIG as any).allowedApps as AuthorizedApp[];
    const { currentURL, ssoParams } = extractSSOParams();

    const queryXtiId = +(currentURL.searchParams.get('targetXti') || 0);
    const queryNext = currentURL.searchParams.get('next');

    let xtiId = queryXtiId;
    let authApp = allowedApps.find(a => a.xtiId === queryXtiId)!;
    if ((queryXtiId === 0) && (allowedApps.length === 1)) {
      authApp = allowedApps[0];
    }

    if (authApp) {
      xtiId = authApp.xtiId;
    }

    if (authApp.dllPath) {
      // this app has its own DLL, start talking to that DLL from now on - we want to log into the app's DLL, not ours
      SB_CONFIG.serverURL = authApp.dllPath + 'rest';
    }

    const relativeUrl = authApp.appPath;
    let base = window.location.origin + window.location.pathname;
    if (!base.endsWith('/')) {
      base += '/';
    }
    const url = new URL(relativeUrl, base).href;

    apiLoginSSO(xtiId, buildSSOBody(ssoParams), url)
      .then(r => {
        let targetUrl = (queryNext && decodeURIComponent(queryNext)) || url;
        if (process.env.NODE_ENV === 'development') {
          // if we're debugging our session token local storage won't work,
          // so pass in as a query parameter. Could in theory always do this?
          // but for now relying on local storage like normal login does.
          targetUrl = targetUrl + '?session=' + r.result.token; //sessionTokenQueryParamName
        }
        // redirect to app
        window.location.href = targetUrl;
      })
      .catch(e => {
        console.error(e);
        if (e.code === ResponseCodes.sbUnauthorized) {
          dialogOk({
            title: _t('Login Failed'),
            message: e.errorMessage || _t('SSO token was invalid.'),
            dialogType: DialogType.Error,
          }).then(() => {
            // just open the normal login screen after instead, which will happen if we navigate to our address
            // but without the sso params.
            window.location.href = currentURL.href;
          });
        } else {
          dialogOk({
            title: _t('Unknown Error'),
            message: _t('Something went wrong. Please try again later.'),
            dialogType: DialogType.Error,
          }).then(() => {
            // As above, just open the normal login screen after instead.
            window.location.href = currentURL.href;
          });
        }
      });
  }, []);

  return <Spinner></Spinner>;
};
